/**
 * Money (phase 3, 03 §4.3, 05 §3.2, 12 §2/§3.2). Stripe Checkout takes the non-refundable deposit and
 * saves the card for the balance; the webhook, inside one transaction, records the payment and converts
 * the hold. A payment refused at conversion (the hold was gone) is still `succeeded`, with
 * `bookingId: null` and a `refusedReason`; the refund is a staff action in Stripe, never a tool.
 * Amounts are integer cents in the venue's currency. Carries `venueId` (server-bound, read-only).
 */
import { z } from 'zod';
import { Currency, Email, Id, Instant, NonNegativeCents, Percent, Timestamps } from './common.js';
import { DepositComponent } from './quote.js';
export const PaymentKind = z.enum(['deposit', 'balance', 'extra', 'refund']);
export const PaymentStatus = z.enum(['pending', 'requires_action', 'succeeded', 'failed', 'refunded']);
/** `link` = a Stripe payment link emailed to the guest (card or ACH, the guest chooses there). */
export const PaymentMethod = z.enum(['card', 'ach', 'link']);
/** `memory` walks the whole flow locally without Stripe (`PAYMENTS_DRIVER`). */
export const PaymentProvider = z.enum(['stripe', 'memory']);
/** Why a succeeded deposit did not become a booking. */
export const PaymentRefusedReason = z.enum(['hold_gone', 'date_taken']);
export const Payment = z.object({
    id: Id,
    venueId: Id,
    /** Null for a deposit refused at conversion (and while a Checkout session is still open). */
    bookingId: Id.nullable(),
    leadId: Id,
    /** The signed agreement a deposit pays for; null for balance/extra/refund rows. */
    agreementId: Id.nullable(),
    kind: PaymentKind,
    /** Positive, a refund included; `kind` gives the direction. Card fee included when charged. */
    amountCents: NonNegativeCents,
    currency: Currency,
    status: PaymentStatus,
    method: PaymentMethod,
    provider: PaymentProvider,
    providerPaymentIntentId: z.string().max(255).nullable(),
    providerCheckoutSessionId: z.string().max(255).nullable(),
    /** Stripe's hosted receipt; null for memory payments and until it exists. */
    receiptUrl: z.url().nullable(),
    failureCode: z.string().max(100).nullable(),
    /** Stripe's decline message, shown to staff; never guest text. */
    failureMessage: z.string().max(1000).nullable(),
    /** The card-fee pass-through part of `amountCents` (0 when off or ACH). */
    cardFeeCents: NonNegativeCents,
    refusedReason: PaymentRefusedReason.nullable(),
    /**
     * What a deposit paid for, part by part, as Checkout charged it (the deposit and any installments
     * folded into it; card fee excluded). Null for other kinds and for deposits taken before it existed.
     */
    depositComponents: z.array(DepositComponent).nullish(),
    succeededAt: Instant.nullable(),
    ...Timestamps,
});
/** A guest's own payment, as listed by `GET /g/bookings/:id/receipt`. */
export const GuestPayment = Payment.pick({
    id: true,
    kind: true,
    amountCents: true,
    currency: true,
    status: true,
    method: true,
    cardFeeCents: true,
    receiptUrl: true,
    succeededAt: true,
});
// ── Venue payment settings (settings.payments, 12 §3.2/§3.6) ──────────────
/**
 * @deprecated Not read by the API. The card-fee pass-through (02 §5.3, 07 B14) has one source: the
 * pricing rule's `cardFeePassThrough` (Content → Pricing), which the quote prices as its `cardFee` line
 * and Checkout and the balance charge from (`Payment.cardFeeCents`). Kept so stored settings and older
 * clients still parse.
 */
export const CardFeeSettings = z.object({
    enabled: z.boolean(),
    percent: Percent,
    achExempt: z.boolean(),
});
/** A platform fee is capped at 10 % (1000 basis points): a sanity bound, not a pricing decision. */
export const MAX_PLATFORM_FEE_BPS = 1000;
export const PaymentSettings = z.object({
    /** @deprecated Ignored: the pricing rule's `cardFeePassThrough` is the card fee (see `CardFeeSettings`). */
    cardFee: CardFeeSettings,
    /** Where the BEO goes (PLACEHOLDER until Daniel gives the kitchen's address). */
    kitchenEmail: Email.nullable(),
    /**
     * Read-only, derived: the venue's connected Stripe account (`StripeConnection.accountId`), null when
     * none. Ignored on PUT; the account changes only through the Connect handshake.
     */
    stripeAccountId: z.string().max(255).nullable().default(null),
    /**
     * The platform's cut of a deposit, in basis points of the Checkout total, sent as Stripe's
     * `application_fee_amount` only when > 0. Default 0 (no platform fee, 05 §3.2); owner-only (the PUT).
     */
    platformFeeBps: z.number().int().min(0).max(MAX_PLATFORM_FEE_BPS).default(0),
});
export const DEFAULT_PAYMENT_SETTINGS = {
    cardFee: { enabled: false, percent: 0, achExempt: true },
    kitchenEmail: null,
    stripeAccountId: null,
    platformFeeBps: 0,
};
// ── Stripe Connect (Standard accounts, one per venue) ─────────────────────
/** Why a venue's Stripe account is no longer connected: staff disconnected it, or the venue did in Stripe. */
export const StripeDisconnectReason = z.enum(['staff', 'deauthorized']);
/**
 * `GET /s/integrations/stripe`: the venue's connected Stripe account (Connect Standard, OAuth). The
 * platform drives it with its own key and `Stripe-Account`; no token of the venue's is stored, so none
 * is ever returned. `livemode` false = a test-mode account (or the memory driver's `acct_mem_*`).
 */
export const StripeConnection = z.object({
    connected: z.boolean(),
    accountId: z.string().max(255).nullable(),
    /** The account's business name in Stripe, when it has one. */
    businessName: z.string().max(500).nullable(),
    livemode: z.boolean().nullable(),
    connectedAt: Instant.nullable(),
    /** The staff member who completed the handshake (name). */
    connectedBy: z.string().nullable(),
    /** Set when the last connection ended; `deauthorized` = disconnected from Stripe's side. */
    disconnectedAt: Instant.nullable(),
    disconnectReason: StripeDisconnectReason.nullable(),
});
export const STRIPE_NOT_CONNECTED = {
    connected: false,
    accountId: null,
    businessName: null,
    livemode: null,
    connectedAt: null,
    connectedBy: null,
    disconnectedAt: null,
    disconnectReason: null,
};
/**
 * How the handshake ended, as `?stripe=` on the console's Settings → Payments after the callback:
 * `connected`; `cancelled` (the owner declined in Stripe); `expired` (the state was older than 10
 * minutes or did not verify); `taken` (that Stripe account is connected to another venue); `forbidden`
 * (the person who started it is no longer an owner here); `failed` (Stripe refused the code).
 */
export const StripeConnectResult = z.enum([
    'connected',
    'cancelled',
    'expired',
    'taken',
    'forbidden',
    'failed',
]);
//# sourceMappingURL=payments.js.map